import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import PasswordField from '../components/ui/PasswordField';
import { emailValidationMessage, normalizeEmail } from '../utils/emailValidation';
import { safeNextPath } from '../utils/authRedirect';
import { LegalFooterLinks } from '../components/legal/LegalDocument';
import RecaptchaWidget from '../components/auth/RecaptchaWidget';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const getCaptchaTokenRef = useRef(null);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const registerHref = next !== '/dashboard' ? `/register?next=${encodeURIComponent(next)}` : '/register';

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, navigate, next]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = emailValidationMessage(email);
    if (msg) {
      toast.error(msg);
      return;
    }
    const normalized = normalizeEmail(email);
    setLoading(true);
    try {
      await login(normalized, password, getCaptchaTokenRef.current?.() || undefined);
      navigate(next, { replace: true });
    } catch (err) {
      const status = err?.status ?? err?.response?.status;
      if (status === 403) {
        toast.error('Please verify your email before signing in');
        navigate(`/verify-email?email=${encodeURIComponent(normalized)}`, { replace: true });
      } else if (status === 401) {
        toast.error('Email or password are unknown to us');
      } else if (status === 0 || status >= 500) {
        toast.error('Server unreachable — please try again shortly');
      } else {
        toast.error(err?.message || err?.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8">
        <Link to="/" className="block">
          <img src="/propscope-logo.png" alt="PropScope"
            className="h-24 w-auto object-contain mx-auto mb-2 hover:opacity-90 transition-opacity" />
        </Link>
        <p className="text-slate-500 text-xs tracking-wide text-center mb-8">Analyse Smarter. Invest Better.</p>
        <h1 className="text-xl font-semibold text-white mb-6 text-center">Sign in</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="block text-sm text-slate-400 mb-1">Email</label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
            />
          </div>
          <PasswordField
            id="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <RecaptchaWidget onReady={(getter) => { getCaptchaTokenRef.current = getter; }} disabled={loading} />
          <div className="flex justify-between text-sm">
            <Link to="/verify-email" className="text-emerald-400 hover:underline">
              Resend verification email
            </Link>
            <Link to="/forgot-password" className="text-emerald-400 hover:underline">
              Forgot password?
            </Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg py-2.5 font-medium transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        <p className="text-center text-slate-500 text-sm mt-4">
          Don&apos;t have an account?{' '}
          <Link to={registerHref} className="text-emerald-400 hover:underline">Create a free account</Link>
        </p>
        <p className="text-center text-slate-600 text-sm mt-3">
          <Link to="/" className="hover:text-slate-400">Homepage</Link>
        </p>
        <LegalFooterLinks className="mt-4" />
      </div>
    </div>
  );
}
